import { useEffect } from 'react'

export type ComponentKeyListenerActionMap = {
  [key in KeyboardEvent['key']]?: (event: KeyboardEvent) => void
}

/**
 * Attaches a keydown event listener to a specified component,
 * allowing you to map specific key events to corresponding actions.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const componentRef = useRef(null);
 *
 *   useComponentKeyListener(componentRef, {
 *     ArrowUp: () => console.log('Up arrow pressed'),
 *     ArrowDown: () => console.log('Down arrow pressed'),
 *   });
 *
 *   return <div ref={componentRef}>Focus and press keys</div>;
 * };
 * ```
 *
 * @important Ensure that the component has focus for the key events to be captured.
 */
export const useComponentKeyListener = (
  /** A ref object pointing to the component you want to attach the key listener to. */
  elementRef: React.RefObject<HTMLElement>,
  /** An object mapping keyboard keys to corresponding action functions. */
  keyActionMap: ComponentKeyListenerActionMap,
) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const action = keyActionMap[event.key] ?? keyActionMap[event.code]
      if (action) {
        action(event)
        event.preventDefault()
      }
    }

    const element = elementRef.current
    if (element) {
      element.addEventListener('keydown', handleKeyDown)
      return () => {
        element.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [elementRef, keyActionMap])
}
